import {Injectable, signal, computed, WritableSignal, Signal, effect, untracked} from '@angular/core';
import {Observable, BehaviorSubject, timer as observableTimer, of} from 'rxjs'; // Renombrado para evitar colisión
import {tap, finalize, take, switchMap, map} from 'rxjs/operators';
import {Factura, FiltrosFactura, ApiResponse} from '@app/types';
import {InvoiceDataService} from '../../services/entities/invoice.service';

@Injectable({
    providedIn: 'root',
})
export class InvoiceMonitorService {
    /**
     * @description WritableSignal holding the current filter criteria.
     */
    public filtrosSignal: WritableSignal<FiltrosFactura> = signal<FiltrosFactura>({});

    /**
     * @description Signal holding the loading state, now directly from the data service.
     */
    public isLoadingSignal: Signal<boolean>;

    private _facturasOriginalesConSeleccion: WritableSignal<Factura[]> = signal<Factura[]>([]);

    /**
     * @description Computed signal that derives the filtered list of invoices.
     * This computation itself does not set isLoadingSignal.
     */
    public facturasFiltradasYSeleccionadasSignal: Signal<Factura[]>;

    /**
     * @description Computed signal that indicates if all currently filtered invoices are selected.
     */
    public todasFiltradasEstanSeleccionadasSignal: Signal<boolean>;

    constructor(private invoiceDataService: InvoiceDataService) {
        this.isLoadingSignal = this.invoiceDataService.isLoadingSignal; // <-- Cambio clave

        // Efecto que copia SIEMPRE las facturas del data service y LIMPIA selección
        effect(() => {
            const facturasBase = this.invoiceDataService.facturasSignal();
            console.log(`PASO 7: [MonitorService EFFECT] - Detectado cambio en facturasSignal. Recibidas ${facturasBase.length} facturas.`);
            
            const normalizadas = facturasBase.map((fb) => ({
                ...fb,
                seleccionada: false,
            }));
            this._facturasOriginalesConSeleccion.set([...normalizadas]);
            console.log(`PASO 7.1: [MonitorService EFFECT] - _facturasOriginalesConSeleccion actualizada con ${normalizadas.length} facturas deseleccionadas.`);
        });

        this.facturasFiltradasYSeleccionadasSignal = computed(() => {
            const facturasOriginales = this._facturasOriginalesConSeleccion();
            const filtros = this.filtrosSignal();
            const searchTerm = (filtros.textoGeneral || '').toLowerCase().trim();

            const getLatestDate = (factura: Factura): Date => {
                if (factura.statusHistory && factura.statusHistory.length > 0) {
                    // El servicio ya convierte las fechas, así que son objetos Date
                    return new Date(Math.max(...factura.statusHistory.map(h => new Date(h.fecha).getTime())));
                }
                return new Date(factura.fechaEmision); // Fallback a la fecha de emisión
            };

            return facturasOriginales
                .filter((factura) => {
                    // Filtros específicos
                    if (filtros.cliente && factura.receptorNombre !== filtros.cliente) {
                        return false;
                    }
                    if (filtros.emisor && factura.emisorNombre !== filtros.emisor) { // <-- NUEVA LÍNEA
                        return false;
                    }
                    if (filtros.rfc && factura.receptorRFC !== filtros.rfc) {
                        return false;
                    }
                    if (filtros.estatus && factura.estatus !== filtros.estatus) {
                        return false;
                    }
                    if (filtros.rangoFechas?.inicio && factura.fechaEmision < filtros.rangoFechas.inicio) {
                        return false;
                    }
                    if (filtros.rangoFechas?.fin) {
                        const fechaFinComparable = new Date(filtros.rangoFechas.fin);
                        fechaFinComparable.setHours(23, 59, 59, 999);
                        if (factura.fechaEmision > fechaFinComparable) {
                            return false;
                        }
                    }

                    // Filtro de texto general robusto
                    if (searchTerm) {
                        const folioCombinado = `${factura.serie || ''}-${factura.folio || ''}`.toLowerCase();
                        if (folioCombinado.includes(searchTerm)) {
                            return true; // Coincidencia directa con serie-folio
                        }

                        const historyText = (factura.statusHistory || [])
                            .map(h => h?.observacion || '')
                            .join('');

                        const searchableText = [
                            factura.folio || '',
                            factura.serie || '',
                            factura.receptorNombre || '',
                            factura.receptorRFC || '',
                            factura.emisorNombre || '',
                            factura.emisorRFC || '',
                            factura.estatus || '',
                            (factura.total != null ? String(factura.total) : ''),
                            factura.ordenSurtimiento?.folioOrden || '',
                            factura.fechaEmision ? factura.fechaEmision.toLocaleDateString('es-MX') : '',
                            historyText,
                        ]
                            .join('')
                            .toLowerCase()
                            .replace(/\s+/g, '');

                        if (!searchableText.includes(searchTerm.replace(/\s+/g, ''))) {
                            return false;
                        }
                    }

                    return true;
                })
                .sort((a, b) => {
                    const dateA = getLatestDate(a);
                    const dateB = getLatestDate(b);
                    return dateB.getTime() - dateA.getTime(); // Orden descendente (más nuevo primero)
                });
        });

        this.todasFiltradasEstanSeleccionadasSignal = computed(() => {
            const facturasFiltradas = this.facturasFiltradasYSeleccionadasSignal();
            return facturasFiltradas.length > 0 && facturasFiltradas.every((f) => f.seleccionada === true);
        });

        // --- CÁLCULO DE OPCIONES DE AUTOCOMPLETADO (LISTA COMPLETA, NO FILTRADA) ---
        (this as any).clientesUnicosSignal = computed(() => {
            const facturas = this._facturasOriginalesConSeleccion();
            const nombres = facturas.map(f => f.receptorNombre || '');
            return [...new Set(nombres)].sort();
        });

        (this as any).emisoresUnicosSignal = computed(() => { // <-- NUEVO BLOQUE
            const facturas = this._facturasOriginalesConSeleccion();
            const nombres = facturas.map(f => f.emisorNombre || '');
            return [...new Set(nombres)].sort();
        });

        (this as any).rfcsUnicosSignal = computed(() => {
            const facturas = this._facturasOriginalesConSeleccion();
            const rfcs = facturas.map(f => f.receptorRFC || '');
            return [...new Set(rfcs)].sort();
        });
        // --- FIN ---
    }

    /**
     * @description Updates the filter values by setting the filtrosSignal.
     * @param nuevosFiltros The new filter criteria.
     */
    public actualizarFiltros(nuevosFiltros: FiltrosFactura): void {
        this.filtrosSignal.set(nuevosFiltros);
    }

    /**
     * @description Toggles the selection state of a specific invoice.
     * Updates the `_facturasOriginalesConSeleccion` signal.
     * @param facturaId The ID of the invoice to toggle.
     * @param seleccionada The new selection state.
     */
    public seleccionarFactura(facturaId: string, seleccionada: boolean): void {
        this._facturasOriginalesConSeleccion.update((facturas) => facturas.map((f) => (f.id === facturaId ? {...f, seleccionada} : f)));
    }

    /**
     * @description Selects or deselects all invoices that are currently visible in the filtered list.
     * Updates the `_facturasOriginalesConSeleccion` signal for the affected invoices.
     * @param seleccionar True to select all visible, false to deselect all visible.
     */
    public seleccionarTodasLasFiltradas(seleccionar: boolean): void {
        const facturasFiltradasActualesIds = untracked(this.facturasFiltradasYSeleccionadasSignal).map((f) => f.id);
        this._facturasOriginalesConSeleccion.update((facturas) => facturas.map((f) => (facturasFiltradasActualesIds.includes(f.id) ? {...f, seleccionada: seleccionar} : f)));
    }

    /**
     * @description Gets the IDs of all currently selected invoices.
     * @returns An array of selected invoice IDs.
     */
    public getFacturasSeleccionadasIds(): string[] {
        return this._facturasOriginalesConSeleccion()
            .filter((f) => f.seleccionada)
            .map((f) => f.id);
    }

    /**
     * @description Changes the status of all selected invoices. Manages loading state.
     * @param nuevoEstatus The new status to apply.
     * @returns Observable<boolean> indicating success of the operation.
     */
    public cambiarEstatusSeleccionadas(nuevoEstatus: string, observacion: string): Observable<ApiResponse<any>> {
      const idsSeleccionadas = this.getFacturasSeleccionadasIds();
      console.log("PASO 2: [MonitorService] - Inicia 'cambiarEstatusSeleccionadas'.");
      console.log("  - IDs a enviar al servicio de datos:", idsSeleccionadas);

      if (idsSeleccionadas.length === 0) {
        return of({ data: [], count: 0, message: 'No hay facturas seleccionadas.', state: 'error' as const });
      }

      return this.invoiceDataService.aplicarCambioEstatus(idsSeleccionadas, nuevoEstatus, observacion).pipe(
        tap((response) => {
          console.log("PASO 8: [MonitorService] - Recibida respuesta de InvoiceDataService.");
          console.log("  - Respuesta:", JSON.stringify(response));
          if (response.state === 'success') {
            // La actualización del estado local ya la hace InvoiceDataService.
            // El effect se encargará de deseleccionar todo.
            console.log("  - La operación fue exitosa. El effect debería actualizar las señales.");
          }
        }),
      );
    }
}
